// src/components/AnonymousUsersList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Badge,
  FormControl,
  FormLabel,
  HStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon, PhoneIcon, EmailIcon } from '@chakra-ui/icons';
import { FiUsers, FiShoppingCart } from 'react-icons/fi';
import axiosInstance from '../../Services/axiosInstance';
import Pagination from '../../components/Pagination';

const AnonymousUsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [pageNumber, setPageNumber] = useState(0);
  const pageSize = 20;
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');

  const cardBg = useColorModeValue('white', 'gray.800');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.750');
  const tableBorder = useColorModeValue('gray.200', 'gray.700');

  const fetchAnonymousUsers = async (page = 0) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/subscribers/anonymous', {
        params: {
          pageNumber: page,
          pageSize,
        },
      });

      const { items, totalCount } = response.data;

      // Sort by mappingTime, most recent first
      const sortedItems = [...(items || [])].sort(
        (a, b) => new Date(b.mappingTime) - new Date(a.mappingTime)
      );

      setUsers(sortedItems);
      setTotalElements(totalCount || 0);
      setTotalPages(Math.ceil((totalCount || 0) / pageSize));
      setPageNumber(page);
    } catch (err) {
      console.error('Failed to fetch anonymous users:', err);
      setError('Failed to load anonymous subscribers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnonymousUsers(0);
  }, []);

  // Client-side filtering (only on current page data)
  const filteredUsers = users.filter((user) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase().trim();

    return (
      (user.phone && user.phone.toLowerCase().includes(term)) ||
      (user.email && user.email.toLowerCase().includes(term)) ||
      (user.cookieUuid && user.cookieUuid.toLowerCase().includes(term)) ||
      (user.fullName && user.fullName.toLowerCase().includes(term))
    );
  });

  const handlePageChange = (newPageZeroBased) => {
    fetchAnonymousUsers(newPageZeroBased);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <Box
      p={{ base: 4, md: 6, lg: 8 }}
      bg={useColorModeValue('gray.50', 'gray.900')}
      minH="100vh"
    >
      <Card
        shadow="xl"
        borderRadius="2xl"
        bg={cardBg}
        overflow="hidden"
        border="1px solid"
        borderColor={tableBorder}
      >
        <CardHeader
          bg={useColorModeValue('purple.50', 'purple.900')}
          py={6}
          px={{ base: 5, md: 8 }}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap={5}>
            <HStack spacing={4}>
              <Icon as={FiUsers} boxSize={7} color="purple.500" />
              <Text
                fontSize={'1rem'}
                color={useColorModeValue('purple.700', 'purple.200')}
              >
                Anonymous Subscribers
              </Text>
            </HStack>
          </Flex>
        </CardHeader>

        <CardBody p={{ base: 5, md: 8 }}>
          {/* Search + Result count */}
          <Flex
            direction={{ base: 'column', sm: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', sm: 'center' }}
            mb={8}
            gap={6}
          >
            {users.length > 0 && (
              <FormControl maxW={{ base: '100%', md: '460px' }}>
                <FormLabel fontSize="sm" mb={2} color="gray.600">
                  Search by name, email, phone or ID
                </FormLabel>
                <InputGroup size="md">
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="e.g. John, xyz@gmail.com, +918686..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    focusBorderColor="purple.500"
                    borderRadius="lg"
                    _placeholder={{ color: 'gray.400' }}
                  />
                </InputGroup>
              </FormControl>
            )}

            {(searchTerm || filteredUsers.length < users.length) && (
              <Text
                fontSize="sm"
                color="gray.500"
                alignSelf={{ base: 'start', sm: 'center' }}
              >
                Showing {filteredUsers.length} of {users.length} record
                {users.length !== 1 ? 's' : ''}
              </Text>
            )}
          </Flex>

          {loading ? (
            <Flex justify="center" align="center" minH="50vh">
              <Spinner
                thickness="4px"
                speed="0.75s"
                emptyColor="gray.200"
                color="purple.500"
                size="xl"
              />
            </Flex>
          ) : error ? (
            <Alert status="error" borderRadius="xl" variant="subtle" py={6}>
              <AlertIcon boxSize={6} mr={4} />
              <Text fontSize="md">{error}</Text>
            </Alert>
          ) : filteredUsers.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={20}
              color="gray.500"
            >
              <Text fontSize="xl" fontWeight="medium" mb={3}>
                {searchTerm
                  ? 'No matching anonymous users found'
                  : 'No anonymous subscribers yet'}
              </Text>
              {searchTerm && (
                <Text fontSize="md">Try a different search term</Text>
              )}
            </Flex>
          ) : (
            <>
              <Box
                overflowX="auto"
                borderRadius="xl"
                border="1px solid"
                borderColor={tableBorder}
                boxShadow="sm"
              >
                <Table variant="simple" size={{ base: 'md', md: 'md' }}>
                  <Thead bg={headerBg}>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Email</Th>
                      <Th>Phone</Th>
                      <Th isNumeric>Orders</Th>
                      <Th>Joined</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredUsers.map((user) => (
                      <Tr
                        key={user.id || user.cookieUuid}
                        _hover={{ bg: hoverBg }}
                        transition="background 0.2s ease"
                      >
                        <Td fontWeight="medium">
                          {user.fullName || '—'}
                        </Td>

                        <Td>
                          <HStack spacing={2}>
                            <EmailIcon color="gray.500" boxSize={4} />
                            <Text>{user.email || '—'}</Text>
                          </HStack>
                        </Td>

                        <Td>
                          <HStack spacing={2}>
                            <PhoneIcon color="gray.500" boxSize={4} />
                            <Text>{user.phone || '—'}</Text>
                          </HStack>
                        </Td>

                        <Td isNumeric fontWeight="medium">
                          <HStack spacing={2} justify="flex-end">
                            <Icon as={FiShoppingCart} color="purple.500" boxSize={4} />
                            <Text>
                              {user.orderCount ?? 0}
                            </Text>
                          </HStack>
                        </Td>

                        <Td
                          whiteSpace="nowrap"
                          color="gray.600"
                          fontSize="sm"
                        >
                          {formatDate(user.mappingTime)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Pagination – only show when not filtering and multiple pages */}
              {!searchTerm && totalPages > 1 && (
                <Flex justify="center" mt={10}>
                  <Pagination
                    totalCount={totalElements}
                    currentPage={pageNumber + 1}
                    pageSize={pageSize}
                    onPageChange={(page1Based) => handlePageChange(page1Based - 1)}
                    isDisabled={loading}
                  />
                </Flex>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

export default AnonymousUsersList;